import { ProductGroup, Wiki } from '@/payload-types'
import { CardProps } from '@/ui/components/Card'
import { CardType } from '@/ui/components/Card/types'

/**
 * Computes "Used in" data for a product group using a pre-built wikiToProcessMap.
 * This avoids relying on wiki.process.docs (join field, not populated on nested docs).
 */
function computeProductGroupUsedIn(
  productGroup: ProductGroup,
  excludeProcessId?: string,
  wikiToProcessMap?: Map<string, Array<{ id: string; title: string }>>,
): { items: Array<{ process: string; wiki: string }>; isAlsoUsedIn: boolean } {
  const usedIn: Array<{ process: string; wiki: string }> = []
  const fullList: Array<{ process: string; wiki: string }> = []

  if (!Array.isArray(productGroup.visibleOn) || !wikiToProcessMap)
    return { items: usedIn, isAlsoUsedIn: false }

  for (const ref of productGroup.visibleOn) {
    if (ref.relationTo !== 'wiki' || typeof ref.value === 'string') continue

    const wiki = ref.value as Wiki
    const processes = wikiToProcessMap.get(wiki.id) ?? []

    for (const process of processes) {
      if (!process.title) continue
      fullList.push({ process: process.title, wiki: wiki.title })
      if (!excludeProcessId || process.id !== excludeProcessId) {
        usedIn.push({ process: process.title, wiki: wiki.title })
      }
    }
  }

  return { items: usedIn, isAlsoUsedIn: fullList.length > usedIn.length }
}

/**
 * Creates a mapper with optional excludeProcessId and wikiToProcessMap
 */
export const createMapProductGroupsToCardProps = (
  excludeProcessId?: string,
  wikiToProcessMap?: Map<string, Array<{ id: string; title: string }>>,
) => {
  return async (productGroups: ProductGroup[]): Promise<CardProps[]> => {
    return productGroups.map((productGroup) => {
      const { items: usedIn, isAlsoUsedIn } = computeProductGroupUsedIn(
        productGroup,
        excludeProcessId,
        wikiToProcessMap,
      )
      return {
        type: CardType.Teaser,
        title: productGroup.title,
        description: productGroup.previewCardFields?.excerpt,
        label: productGroup.category,
        href: productGroup.path,
        image: productGroup.previewCardFields?.thumbnail,
        appearance: 'outlined',
        usedIn,
        isAlsoUsedIn,
      } as CardProps
    })
  }
}

export const mapProductGroupsToCardProps = createMapProductGroupsToCardProps()
